import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  IconButton,
  Chip,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import AbsenceForm from './AbsenceForm';
import { useTechnicians } from '../../contexts/TechniciansContext';
import { useAbsences } from '../../contexts/AbsencesContext';
import { useTeamPermissions } from '../../hooks/useTeamPermissions';
import { useAuth } from '../../context/AuthContext';
import { ABSENCE_TYPES } from '../../constants/absenceTypes';

const AbsencesList = ({ onEdit }) => {
  const { absences, loading, error: contextError, deleteAbsence } = useAbsences();
  const { technicians } = useTechnicians();
  const { canEdit, canDelete } = useTeamPermissions();
  const { currentUser } = useAuth();
  const [techniciansMap, setTechniciansMap] = useState({});
  const [error, setError] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedAbsence, setSelectedAbsence] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Convertir les techniciens en map pour un accès plus facile
  useEffect(() => {
    if (technicians.length > 0) {
      console.log('👥 AbsencesList - Mise à jour de la map des techniciens');
      const techniciansMap = technicians.reduce((acc, tech) => {
        acc[tech.id] = tech;
        return acc;
      }, {});
      setTechniciansMap(techniciansMap);
    }
  }, [technicians]);

  // Mettre à jour l'erreur locale si erreur du contexte
  useEffect(() => {
    if (contextError) {
      setError(contextError);
    }
  }, [contextError]);

  const handleDeleteClick = (absence) => {
    setSelectedAbsence(absence);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedAbsence) return;

    try {
      await deleteAbsence(selectedAbsence.id);
      setDeleteConfirmOpen(false);
      setSelectedAbsence(null);
    } catch (error) {
      setError('Erreur lors de la suppression de l\'absence');
    }
  };

  const formatDate = (date) => {
    return format(new Date(date), 'dd MMMM yyyy', { locale: fr });
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const filteredAbsences = React.useMemo(() => {
    if (!searchQuery) return absences;
    const terms = searchQuery.toLowerCase().split(/\s+/).filter(Boolean);
    return absences.filter((a) => {
      const tech = techniciansMap[a.technicianId];
      const fullName = tech ? `${tech.firstName} ${tech.lastName}`.toLowerCase() : '';
      const email = tech?.email?.toLowerCase() || '';
      const typeCode = (a.type || '').toLowerCase();
      const typeLabel = (ABSENCE_TYPES[a.type]?.label || '').toLowerCase();
      const datesHuman = `du ${formatDate(a.startDate)} au ${formatDate(a.endDate)}`.toLowerCase();
      const datesRaw = `${a.startDate} ${a.endDate}`.toLowerCase();
      const comment = (a.comment || '').toLowerCase();
      const idStr = String(a.id || '').toLowerCase();

      return terms.every((t) =>
        fullName.includes(t) ||
        email.includes(t) ||
        typeCode.includes(t) ||
        typeLabel.includes(t) ||
        datesHuman.includes(t) ||
        datesRaw.includes(t) ||
        comment.includes(t) ||
        idStr.includes(t)
      );
    });
  }, [absences, techniciansMap, searchQuery]);

  if (!currentUser?.id) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="textSecondary">
          Veuillez vous connecter pour voir les absences.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!absences || absences.length === 0) {
    return (
      <Paper>
        <Box sx={{ p: 2 }}>
          <Typography variant="body1" color="textSecondary">
            Aucune absence n'a été enregistrée.
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <>
      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Rechercher par nom, prénom, email, type, dates ou commentaire..."
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          variant="outlined"
          size="small"
        />
      </Paper>

      <Paper>
        <TableContainer>
          <Table>
            <TableBody>
              {filteredAbsences.map((absence) => {
                const technician = techniciansMap[absence.technicianId];
                const absenceType = ABSENCE_TYPES[absence.type] || ABSENCE_TYPES.AUTRE;

                return (
                  <TableRow key={absence.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1">
                          {technician ? `${technician.firstName} ${technician.lastName}` : 'Technicien inconnu'}
                        </Typography>
                        <Chip
                          label={absenceType.label}
                          size="small"
                          sx={{
                            backgroundColor: `${absenceType.color}20`,
                            border: `1px solid ${absenceType.color}40`,
                            color: 'text.secondary',
                            '& .MuiChip-label': {
                              px: 1
                            }
                          }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" component="span" display="block">
                        Du {formatDate(absence.startDate)} au {formatDate(absence.endDate)}
                      </Typography>
                      {absence.comment && (
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ mt: 0.5 }}
                        >
                          {absence.comment}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {canEdit('absences') && (
                          <IconButton
                            edge="end"
                            aria-label="modifier"
                            onClick={() => onEdit(absence)}
                          >
                            <EditIcon />
                          </IconButton>
                        )}
                        {canDelete('absences') && (
                          <IconButton
                            edge="end"
                            aria-label="supprimer"
                            onClick={() => handleDeleteClick(absence)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer cette absence ?
            {selectedAbsence && techniciansMap[selectedAbsence.technicianId] && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {`${techniciansMap[selectedAbsence.technicianId].firstName} ${techniciansMap[selectedAbsence.technicianId].lastName}`}
                  <br />
                  {`Du ${formatDate(selectedAbsence.startDate)} au ${formatDate(selectedAbsence.endDate)}`}
                </Typography>
              </Box>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Annuler</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      {error && (
        <Alert 
          severity="error"
          onClose={() => setError(null)}
          sx={{ mt: 2 }}
        >
          {error}
        </Alert>
      )}
    </>
  );
};

export default AbsencesList; 